<script setup lang="ts">
//
// Imports
//

import markdownit from "markdown-it";
import { useAuthClient } from "~/lib/auth-client";

//
// Composables
//

// Auth Composable
const { logIn } = useAuthClient();

//
// Initial Setup
//

// Props
const props = defineProps({
  // The terms of use of the laboratory
  laboratoryTermsOfUse: {
    type: String,
    required: true,
  },

  // Indicates if the laboratory terms of use is loading
  loadingLabTermsOfUse: {
    type: Boolean,
    required: true,
  },
});

// Localization
const { t } = useI18n();
const localePath = useLocalePath();

//
// Reactive State
//

// Terms Of Use
const isAgreed = ref(true);
const showTermsDialog = ref(false);

// Markdown
const md = markdownit();

// Reactive variable for parsed HTML content from fetched Markdown
const parsedTermsContent = computed(() => {
  return props.laboratoryTermsOfUse
    ? md.render(props.laboratoryTermsOfUse)
    : "";
});

//
// Methods
//

// Authentication logic
function login() {
  // Redirect to a loading page where roles will be fetched and processed
  logIn(localePath(window.location.origin + "/loading"));
}
</script>

<template>
  <!-- Main Section -->
  <v-container class="text-start mt-4 mt-sm-16 mx-0" max-width="1024">
    <v-row
      class="d-flex my-0 mt-sm-8 py-0 align-center"
      justify="space-between"
    >
      <!-- Title Section -->
      <v-col cols="12" md="6" class="pa-0">
        <!-- Welcome Text -->
        <h1 class="text-h4 text-sm-h3 font-weight-bold mb-2">
          {{ t("landing_page.main_section.welcome_message.title") }}

          <span class="text-primary">
            {{ t("landing_page.main_section.welcome_message.subtitle") }}
          </span>
        </h1>

        <!-- Description And Checkbox -->
        <div class="d-flex flex-column align-start">
          <p class="text-body-2 text-sm-body-1">
            {{ t("landing_page.main_section.welcome_message.description") }}
          </p>

          <!-- Terms of use & Privacy Policy Checkbox -->
          <v-checkbox
            v-model="isAgreed"
            class="text-caption"
            color="primary"
            hide-details
            style="transform: scale(0.8); height: 60px; margin-left: -35px"
          >
            <template v-slot:label>
              <div>
                {{ t("landing_page.main_section.terms.i_agree_with_the") }}
                <client-only>
                  <v-tooltip
                    location="bottom"
                    :open-delay="700"
                    :close-delay="300"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="text-primary text-body-2 font-weight-bold cursor-pointer"
                        @click.stop="
                          isAgreed = false;
                          showTermsDialog = true;
                        "
                      >
                        {{ t("landing_page.main_section.terms.terms_of_use") }}
                      </span>
                    </template>

                    <!-- Tooltip text -->
                    <span>{{ t("tooltips.landing.terms_of_use") }}</span>
                  </v-tooltip>
                </client-only>

                <!-- Dialog for Terms of Use -->
                <v-dialog
                  v-model="showTermsDialog"
                  width="600"
                  persistent
                  scrollable
                >
                  <v-card>
                    <!-- Render the parsed Markdown content -->
                    <v-card-text>
                      <div v-html="parsedTermsContent"></div>
                    </v-card-text>

                    <v-card-actions>
                      <v-btn
                        variant="flat"
                        color="primary"
                        rounded="lg"
                        size="large"
                        class="w-100 font-weight-bold"
                        @click="showTermsDialog = false"
                      >
                        OK
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </div>
            </template>
          </v-checkbox>
        </div>

        <!-- Landing Buttons -->
        <v-row justify="start" class="d-flex ga-2 mx-0 mb-4 align-center">
          <!-- Log In Button -->
          <v-btn
            @click="login()"
            :disabled="!isAgreed"
            color="primary"
            rounded="lg"
            variant="flat"
            class="font-weight-bold"
            aria-label="Go to log in"
            prepend-icon="mdi-login-variant"
            :v-tooltip.bottom="t('tooltips.landing.log_in')"
          >
            {{ t("landing_page.main_section.login_button") }}
          </v-btn>
        </v-row>
      </v-col>

      <!-- Landing Image -->
      <v-col class="mt-n4" style="padding: 0 1px">
        <v-img
          src="/images/landing2.svg"
          alt="Web Portal Image"
          style="width: auto; height: 100%"
        ></v-img>
      </v-col>
    </v-row>
  </v-container>
</template>
